import { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { withErrorBoundary } from './components/error-boundary/with-error-boundary';
import { TooltipProvider } from './components/ui/tooltip';
import { Provider } from 'jotai';
import store from './stores';
import { getLocale } from './utils/locale';

import { isRegisterEnabled } from './utils';
import Loading from './components/loading';
import '@ant-design/v5-patch-for-react-19';
import AntdProvider from './components/antd-provider';
import 'dayjs/locale/zh-cn';

// layouts
import AuthLayout from './layouts/auth-layout';
import MainLayout from './layouts/main-layout';
import NotFound from './pages/404';

import { setDefaultOptions } from 'date-fns';
import NormalLayout from './layouts/normal-layout.tsx';

setDefaultOptions({ locale: getLocale() });

// pages
const Demo = lazy(() => import('./pages/demo'));
const Login = withErrorBoundary(lazy(() => import('./pages/auth/login')));
const LarkLogin = withErrorBoundary(lazy(() => import('./pages/auth/lark-login')));
const OpenAuthLogin = withErrorBoundary(lazy(() => import('./pages/auth/oauth2/index')));
const OpenConfirmAuth = withErrorBoundary(lazy(() => import('./pages/auth/oauth2/confirm')));
const Index = withErrorBoundary(lazy(() => import('./pages/index')));
const Home = withErrorBoundary(lazy(() => import('./pages/home')));
const DataDaily = withErrorBoundary(lazy(() => import('./pages/home/data-daily')));
const Chat = withErrorBoundary(lazy(() => import('./pages/chat')));
const AISetting = withErrorBoundary(lazy(() => import('./pages/channel/ai-setting/index.tsx')));
const Channel = withErrorBoundary(lazy(() => import('./pages/channel/index.tsx')));
const AIAgent = withErrorBoundary(lazy(() => import('./pages/channel/ai-agent/index.tsx')));
const AIKnowledgeDetail = withErrorBoundary(lazy(() => import('./pages/channel/detail/index.tsx')));
const Register = withErrorBoundary(lazy(() => import('./pages/auth/register')));
const ChannelAuth = withErrorBoundary(lazy(() => import('./pages/channel-auth')));
const BI = withErrorBoundary(lazy(() => import('./pages/bi/index.tsx')));
const Knowledge = withErrorBoundary(lazy(() => import('./pages/knowledge')));
const KnowledgeDetail = withErrorBoundary(lazy(() => import('./pages/knowledge/detail/index.tsx')));

const Tag = withErrorBoundary(lazy(() => import('./pages/setting/tag')));
const AutomationV2 = withErrorBoundary(
  lazy(() => import('./pages/setting/automation/indexV2.tsx')),
);
const AutomationDetail = withErrorBoundary(lazy(() => import('./pages/setting/automation/detail')));
const AutomationEditor = withErrorBoundary(
  lazy(() => import('./pages/setting/automation/detailV2')),
);
const AutomationReceptionFlowManager = withErrorBoundary(
  lazy(() => import('./pages/setting/automation/ReceptionFlowManager')),
);

const SendCardRule = withErrorBoundary(
  lazy(() => import('./pages/channel/send-card-rule/index.tsx')),
);
const MoreTemplate = withErrorBoundary(
  lazy(() => import('./pages/setting/automation/templateV2/more-template')),
);
const PaidManagement = withErrorBoundary(lazy(() => import('./pages/paid-management')));
const PointPurchase = withErrorBoundary(lazy(() => import('./pages/paid-management/point')));
const Expansion = withErrorBoundary(lazy(() => import('./pages/paid-management/expansion')));
const OpenPlan = withErrorBoundary(lazy(() => import('./pages/paid-management/plan-package')));
const RenewPlan = withErrorBoundary(
  lazy(() => import('./pages/paid-management/plan-package/renew.tsx')),
);
const UpgradePlan = withErrorBoundary(
  lazy(() => import('./pages/paid-management/plan-package/upgrade.tsx')),
);
const OrderPay = withErrorBoundary(lazy(() => import('./pages/paid-management/order-pay')));
const OrderList = withErrorBoundary(lazy(() => import('./pages/paid-management/order-list')));
const BillList = withErrorBoundary(lazy(() => import('./pages/paid-management/bill-list')));
const CardManage = withErrorBoundary(lazy(() => import('./pages/channel/card-manage')));
const Setting = withErrorBoundary(lazy(() => import('./pages/setting/setting')));
const Identity = withErrorBoundary(lazy(() => import('./pages/identify')));

const authRouter = [
  { path: '/login', element: <Login /> },
  { path: '/lark-login', element: <LarkLogin /> },
  { path: '/channel-auth', element: <ChannelAuth /> },
  { path: '/oauth2/login', element: <OpenAuthLogin /> },
  { path: '/oauth2/confirm', element: <OpenConfirmAuth /> },
];
if (isRegisterEnabled()) {
  authRouter.push({ path: '/register', element: <Register /> });
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <MainLayout />,
    children: [
      { index: true, element: <Index /> },
      {
        path: '/home',
        children: [
          { index: true, element: <Home /> },
          { path: 'daily', element: <DataDaily /> },
        ],
      },
      { path: '/chat', element: <Chat /> },
      {
        path: '/channel',
        children: [
          { index: true, element: <Channel /> },
          {
            path: ':type/:id/:source',
            children: [
              { index: true, element: <AIKnowledgeDetail /> },
              { path: 'setting', element: <AISetting /> },
              { path: 'ai-agent', element: <AIAgent /> },
              { path: 'send-card-rule', element: <SendCardRule /> },
              { path: 'card-manage', element: <CardManage /> },
            ],
          },
        ],
      },
      { path: '/bi', element: <BI /> },
      { path: '/knowledge', element: <Knowledge /> },
      { path: '/knowledge/:id', element: <KnowledgeDetail /> },
      { path: '/tag', element: <Tag /> },
      {
        path: '/automation',
        children: [
          { path: 'reception', element: <AutomationReceptionFlowManager /> },
          { index: true, element: <AutomationV2 /> },
          { path: 'detail-v1', element: <AutomationDetail /> },
          { path: 'detail', element: <AutomationEditor /> },
          { path: 'more', element: <MoreTemplate /> },
        ],
      },
      {
        path: '/setting',
        children: [
          { index: true, element: <Setting /> },
          { path: 'company', element: <Setting /> },
          { path: 'customer', element: <Setting /> },
          { path: 'quick-reply', element: <Setting /> },
          { path: 'agent', element: <Setting /> },
          { path: 'role', element: <Setting /> },
          { path: 'reception', element: <Setting /> },
        ],
      },
      {
        path: '/paid-management',
        children: [
          { index: true, element: <PaidManagement /> },
          { path: 'point', element: <PointPurchase /> },
          { path: 'expansion', element: <Expansion /> },
          { path: 'open', element: <OpenPlan /> },
          { path: 'renew', element: <RenewPlan /> },
          { path: 'upgrade', element: <UpgradePlan /> },
          { path: 'order-pay', element: <OrderPay /> },
          { path: 'order-list', element: <OrderList /> },
          { path: 'bill-list', element: <BillList /> },
        ],
      },
    ],
  },
  { element: <AuthLayout />, children: authRouter },
  { element: <NormalLayout />, children: [{ path: '/identify', element: <Identity /> }] },
  { path: '/demo', element: <Demo /> },

  { path: '*', element: <NotFound /> },
]);

function AppComponent() {
  return (
    <AntdProvider>
      <Provider store={store}>
        <TooltipProvider delayDuration={200}>
          <Suspense key="app" fallback={<Loading className="h-screen" tip="页面加载中..." />}>
            <RouterProvider router={router} />
          </Suspense>
        </TooltipProvider>
      </Provider>
    </AntdProvider>
  );
}

const App = withErrorBoundary(AppComponent);
export default App;
